import {useFieldConfigurationContext} from '@hconnect/tuc-processes/configuration/useConfigurationContext'
import {useDateTime} from '@hconnect/tuc-processes/fields/dateTime/useDateTime'
import {useOrderChangeContext} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {Alert, Button} from '@hm/fundamental'
import {Close} from '@mui/icons-material'
import {Box, Typography, IconButton} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useChangeOrder} from '../../hooks/useChangeOrder'
import {useFinalReviewItems} from '../../hooks/useFinalReviewItems'
import {OrderChangeResultType} from '../../types/orderChangeResult.types'
import {ErrorModal} from '../ErrorModal/ErrorModal'
import {TimezoneBanner} from '../TimezoneBanner'

import {FinalReviewItem} from './finalReviewItem/FinalReviewItem'

interface FinalReviewProps {
  onBack?: () => void
  onClose?: () => void
  /** Callback when order change is submitted successfully, passes the result type for the result screen */
  onSubmitSuccess?: (resultType: OrderChangeResultType) => void
  jobSiteInfo?: string
}

/**
 * FinalReview Component
 *
 * Displays the final review screen showing all order changes before submission.
 * Users can review all modifications, request the order change, go back to editing, or discard.
 *
 * This component is similar in structure to FieldEditorOverview but focused on
 * the final confirmation step.
 */
export const FinalReview: React.FC<FinalReviewProps> = ({
  onBack,
  onClose,
  onSubmitSuccess,
  jobSiteInfo
}) => {
  const {t} = useTranslation()
  const {canSubmitChanges} = useOrderChangeContext()
  const reviewItems = useFinalReviewItems()

  const {submit, isLoading, errorModalState, closeErrorModal, retrySubmit, handleRefresh} =
    useChangeOrder({
      onResult: (resultType) => onSubmitSuccess?.(resultType)
    })

  // Get timezone info for the time field banner
  const {dateTime} = useDateTime()
  const dateTimeConfiguration = useFieldConfigurationContext('dateTime')
  const jobSiteTimezone = dateTimeConfiguration?.businessHours.timeZone
  const selectedDate = dateTime?.[0] ?? null

  // Belt and braces: useFinalReviewItems already normalises this, but the invariant is not
  // expressible in the type, and not every item construction site goes through that loop.
  const requiresDispatchApproval = useMemo(() => {
    return reviewItems.some((section) =>
      section.items.some((item) => item.requiresApproval && item.hasChanges)
    )
  }, [reviewItems])

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden'}}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          flex: 1,
          overflowY: 'auto',
          p: 3
        }}
        data-test-id="final-review"
      >
        {/* Header */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                lineHeight: '24px',
                color: '#29333d',
                fontFamily: 'Inter'
              }}
            >
              {t('orderIntake.rmc.orderManagement.finalReview', 'Final review')}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                padding: '4px',
                color: '#52667a'
              }}
              data-test-id="close-final-review-button"
            >
              <Close sx={{fontSize: '16px'}} />
            </IconButton>
          </Box>
          {jobSiteInfo && (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '16px',
                color: '#52667a',
                fontFamily: 'Inter'
              }}
            >
              {jobSiteInfo}
            </Typography>
          )}
        </Box>

        {/* Dispatch Approval Banner */}
        {requiresDispatchApproval && (
          <Alert variant="warning" data-test-id="dispatch-approval-banner">
            {t(
              'orderIntake.rmc.orderManagement.dispatchApprovalBanner',
              'The order needs to be approved by our dispatch team.'
            )}
          </Alert>
        )}

        {/* Content Area - Field Review Items organized by sections */}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
          {reviewItems.map((section) => (
            <Box
              key={section.sectionId}
              sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}
            >
              {/* Section Header */}
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '20px',
                  color: '#29333d',
                  fontFamily: 'Inter',
                  letterSpacing: '0.5px'
                }}
              >
                {section.sectionLabel}
              </Typography>

              {/* Section Items */}
              {section.items.map((item) => (
                <Box key={item.fieldId} sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <FinalReviewItem
                    label={item.label}
                    value={item.values}
                    requiresApproval={item.requiresApproval}
                    testId={`final-review-item-${item.fieldId}`}
                  />
                  {/* Show timezone banner after time field */}
                  {item.fieldId === 'time' && (
                    <TimezoneBanner
                      jobSiteTimezone={jobSiteTimezone}
                      selectedDate={selectedDate}
                      data-test-id="final-review-timezone-banner"
                    />
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0, p: 3, borderTop: '1px solid #e0e0e0'}}>
        <Button
          variant="primary"
          size="large"
          onClick={submit}
          disabled={!canSubmitChanges || isLoading}
          className="w-full"
          data-test-id="request-order-change-button"
        >
          {isLoading
            ? t('orderIntake.rmc.orderManagement.submitting', 'Submitting...')
            : t('orderIntake.rmc.orderManagement.requestOrderChange', 'Request order change')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={onBack}
          disabled={isLoading}
          className="w-full"
          data-test-id="go-back-to-editing-button"
        >
          {t('orderIntake.rmc.orderManagement.goBackToEditing', 'Go back to editing')}
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={onClose}
          disabled={isLoading}
          className="w-full"
          data-test-id="discard-and-close-button"
        >
          {t('orderIntake.rmc.orderManagement.discardAndClose', 'Discard and close')}
        </Button>
      </Box>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModalState.isOpen}
        variant={errorModalState.variant}
        message={errorModalState.message}
        onPrimaryAction={
          errorModalState.variant === 'retryable'
            ? retrySubmit
            : errorModalState.variant === 'checksum'
              ? handleRefresh
              : closeErrorModal
        }
        onSecondaryAction={errorModalState.variant === 'retryable' ? closeErrorModal : undefined}
      />
    </Box>
  )
}
